import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Chip from '@material-ui/core/Chip';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';

const useStyles = makeStyles(() => ({
  root: { margin: 22, padding: 20 },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  filterLabel: { fontSize: '0.85rem', fontWeight: 500, color: '#555' },
  filterSelect: { minWidth: 160 },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: 12,
    marginTop: 24,
    color: '#333',
  },
  sectionPaper: { padding: 16, marginBottom: 20 },
  revenueCard: {
    padding: 20,
    borderRadius: 8,
    textAlign: 'center',
    color: '#fff',
    minHeight: 100,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  revenueValue: { fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.2 },
  revenueLabel: { fontSize: '0.8rem', marginTop: 4, opacity: 0.9 },
  tableHead: {
    backgroundColor: '#f5f5f5',
    '& th': { fontWeight: 600, fontSize: '0.8rem', color: '#333' },
  },
  tableCell: { fontSize: '0.85rem' },
  totalRow: {
    '& td': { fontWeight: 700, fontSize: '0.85rem', borderTop: '2px solid #333' },
  },
  paymentBar: {
    display: 'flex',
    borderRadius: 6,
    overflow: 'hidden',
    height: 28,
    marginBottom: 12,
  },
  paymentSegment: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 600,
    minWidth: 40,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 8,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.8rem',
    color: '#555',
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
}));

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtUGX = (amount) =>
  `UGX ${amount.toLocaleString('en-UG')}`;

// ── Mock Data ────────────────────────────────────────────────────────────────
const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const REVENUE_SUMMARY = {
  totalBilled: 248500000,
  totalCollected: 195200000,
  outstanding: 53300000,
  insuranceClaims: 82400000,
};

const REVENUE_BY_CATEGORY = [
  { category: 'Theatre Fees', billed: 72000000, collected: 58000000 },
  { category: 'Surgeon Fees', billed: 64000000, collected: 52800000 },
  { category: 'Anaesthesia Fees', billed: 38500000, collected: 32200000 },
  { category: 'Consumables', billed: 45000000, collected: 35400000 },
  { category: 'Ward Charges', billed: 29000000, collected: 16800000 },
];

const PAYMENT_METHODS = [
  { method: 'Cash', amount: 82700000, color: '#4CAF50' },
  { method: 'Insurance', amount: 82400000, color: '#2196F3' },
  { method: 'Credit', amount: 30100000, color: '#FF9800' },
];

const TOP_SURGERIES = [
  { patient: 'Mukasa Robert', procedure: 'Total Hip Replacement', surgeon: 'Dr. Okello Patrick', amount: 12500000 },
  { patient: 'Nalubega Sarah', procedure: 'Cardiac Bypass (referred)', surgeon: 'Dr. Mugisha James', amount: 9800000 },
  { patient: 'Ssemakula Joseph', procedure: 'Spinal Fusion', surgeon: 'Dr. Okello Patrick', amount: 8500000 },
  { patient: 'Achieng Betty', procedure: 'Total Abdominal Hysterectomy', surgeon: 'Dr. Akullu Esther', amount: 6200000 },
  { patient: 'Opio Charles', procedure: 'Nephrectomy', surgeon: 'Dr. Namutebi Florence', amount: 5800000 },
];

const MONTHLY_TREND = [
  { month: 'Mar 2026', billed: 215000000, collected: 172000000, outstanding: 43000000 },
  { month: 'Apr 2026', billed: 232000000, collected: 188000000, outstanding: 44000000 },
  { month: 'May 2026', billed: 248500000, collected: 195200000, outstanding: 53300000 },
];

// ── Component ────────────────────────────────────────────────────────────────
const FinancialReport = () => {
  const classes = useStyles();
  const [dateRange, setDateRange] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const paymentTotal = PAYMENT_METHODS.reduce((s, p) => s + p.amount, 0);
  const categoryTotalBilled = REVENUE_BY_CATEGORY.reduce((s, c) => s + c.billed, 0);
  const categoryTotalCollected = REVENUE_BY_CATEGORY.reduce((s, c) => s + c.collected, 0);

  return (
    <div>
      <PageTitle title="Financial Report" />
      <SubHeader title="Surgery" subTitle="Financial Report" />

      <Paper className={classes.root} elevation={1}>
        {/* ── Date Range Filter ─────────────────────────────────────── */}
        <div className={classes.filterRow}>
          <Typography className={classes.filterLabel}>Period:</Typography>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            variant="outlined"
            size="small"
            className={classes.filterSelect}
          >
            {DATE_RANGES.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
          {dateRange === 'custom' && (
            <>
              <TextField
                type="date"
                label="From"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
              <TextField
                type="date"
                label="To"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </>
          )}
        </div>

        {/* ── Revenue Summary Cards ─────────────────────────────────── */}
        <Grid container spacing={2} style={{ marginBottom: 24 }}>
          {[
            { label: 'Total Billed', value: REVENUE_SUMMARY.totalBilled, color: '#3F51B5' },
            { label: 'Total Collected', value: REVENUE_SUMMARY.totalCollected, color: '#4CAF50' },
            { label: 'Outstanding', value: REVENUE_SUMMARY.outstanding, color: '#F44336' },
            { label: 'Insurance Claims', value: REVENUE_SUMMARY.insuranceClaims, color: '#2196F3' },
          ].map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.label}>
              <Paper className={classes.revenueCard} style={{ backgroundColor: card.color }} elevation={2}>
                <Typography className={classes.revenueValue}>{fmtUGX(card.value)}</Typography>
                <Typography className={classes.revenueLabel}>{card.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Revenue by Category ───────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Revenue by Category</Typography>
        <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 20 }}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell align="right">Billed (UGX)</TableCell>
                <TableCell align="right">Collected (UGX)</TableCell>
                <TableCell align="right">Outstanding (UGX)</TableCell>
                <TableCell align="center">Collection %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {REVENUE_BY_CATEGORY.map((cat) => {
                const outstanding = cat.billed - cat.collected;
                const collPct = Math.round((cat.collected / cat.billed) * 100);
                return (
                  <TableRow key={cat.category}>
                    <TableCell className={classes.tableCell}>{cat.category}</TableCell>
                    <TableCell align="right" className={classes.tableCell}>
                      {cat.billed.toLocaleString('en-UG')}
                    </TableCell>
                    <TableCell align="right" className={classes.tableCell}>
                      {cat.collected.toLocaleString('en-UG')}
                    </TableCell>
                    <TableCell align="right" className={classes.tableCell}>
                      {outstanding.toLocaleString('en-UG')}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${collPct}%`}
                        size="small"
                        style={{
                          backgroundColor: collPct >= 80 ? '#4CAF50' : collPct >= 60 ? '#FF9800' : '#F44336',
                          color: '#fff',
                          fontWeight: 600,
                          height: 22,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow className={classes.totalRow}>
                <TableCell>Total</TableCell>
                <TableCell align="right">{categoryTotalBilled.toLocaleString('en-UG')}</TableCell>
                <TableCell align="right">{categoryTotalCollected.toLocaleString('en-UG')}</TableCell>
                <TableCell align="right">
                  {(categoryTotalBilled - categoryTotalCollected).toLocaleString('en-UG')}
                </TableCell>
                <TableCell align="center">
                  {Math.round((categoryTotalCollected / categoryTotalBilled) * 100)}%
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Payment Method Breakdown ──────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Payment Method Breakdown</Typography>
        <Paper className={classes.sectionPaper} elevation={0} variant="outlined">
          <div className={classes.paymentBar}>
            {PAYMENT_METHODS.map((pm) => {
              const pct = paymentTotal > 0 ? (pm.amount / paymentTotal) * 100 : 0;
              return (
                <div
                  key={pm.method}
                  className={classes.paymentSegment}
                  style={{
                    width: `${pct}%`,
                    backgroundColor: pm.color,
                  }}
                >
                  {Math.round(pct)}%
                </div>
              );
            })}
          </div>
          <div className={classes.legend}>
            {PAYMENT_METHODS.map((pm) => (
              <div key={pm.method} className={classes.legendItem}>
                <span className={classes.legendDot} style={{ backgroundColor: pm.color }} />
                {pm.method}: {fmtUGX(pm.amount)}
              </div>
            ))}
          </div>
        </Paper>

        {/* ── Top 5 Highest-Value Surgeries ─────────────────────────── */}
        <Typography className={classes.sectionTitle}>Top 5 Highest-Value Surgeries</Typography>
        <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 20 }}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Procedure</TableCell>
                <TableCell>Surgeon</TableCell>
                <TableCell align="right">Amount (UGX)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {TOP_SURGERIES.map((s, idx) => (
                <TableRow key={s.patient}>
                  <TableCell className={classes.tableCell}>{idx + 1}</TableCell>
                  <TableCell className={classes.tableCell}>{s.patient}</TableCell>
                  <TableCell className={classes.tableCell}>{s.procedure}</TableCell>
                  <TableCell className={classes.tableCell}>{s.surgeon}</TableCell>
                  <TableCell align="right" className={classes.tableCell} style={{ fontWeight: 600 }}>
                    {s.amount.toLocaleString('en-UG')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Monthly Trend ─────────────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Monthly Trend (Last 3 Months)</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell align="right">Billed (UGX)</TableCell>
                <TableCell align="right">Collected (UGX)</TableCell>
                <TableCell align="right">Outstanding (UGX)</TableCell>
                <TableCell align="center">Collection %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {MONTHLY_TREND.map((m) => {
                const collPct = Math.round((m.collected / m.billed) * 100);
                return (
                  <TableRow key={m.month}>
                    <TableCell className={classes.tableCell} style={{ fontWeight: 500 }}>{m.month}</TableCell>
                    <TableCell align="right" className={classes.tableCell}>
                      {m.billed.toLocaleString('en-UG')}
                    </TableCell>
                    <TableCell align="right" className={classes.tableCell}>
                      {m.collected.toLocaleString('en-UG')}
                    </TableCell>
                    <TableCell align="right" className={classes.tableCell}>
                      {m.outstanding.toLocaleString('en-UG')}
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell}>{collPct}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
};

export default FinancialReport;
